/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { find, isBoolean, isEmpty, map, omit } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import CheckboxField from 'common/forms/CheckboxField';
import { validateCombinedAddressLength, validateZipCode } from 'common/forms/formValidations';
import RadioGroupField from 'common/forms/RadioGroupField';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import { formatZipCode } from 'common/utils';
import { patientActions } from 'store/slices/patientSlice';
import { listPatientOrders, updateOrderAddress } from 'store/thunks/patientThunks';

import useSmsResetWarningDialog from '../ShippingTab/useSmsResetWarningDialog';

const EditOrderAddressButton = ({
  orderId,
  mpi,
  orderAddress,
  deliveryInstructions,
  afterAddressUpdate,
  disabled,
  notificationPreference: notificationPreferenceProp,
}) => {
  const dispatch = useDispatch();

  const patientInfo = useSelector(({ patient }) => patient[mpi]);

  const notificationPreference = patientInfo?.notificationPreferences ?? notificationPreferenceProp;

  const { InfoDialog, showWarning } = useSmsResetWarningDialog();
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [inputAddress, setInputAddress] = useState({ ...orderAddress });
  const [showModal, setShowModal] = useState(false);
  const [showAddressConfirmationModal, setShowAddressConfirmationModal] = useState(false);
  const [savedNewAddress, setSavedNewAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const openDialog = () => setShowModal(true);
  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setInputAddress(orderAddress);
      setSuggestedAddresses([]);
      setShowModal(false);
    }
  };

  const closeDialog = () => {
    setLoading(false);
    setSuggestedAddresses([]);
    setShowAddressConfirmationModal(false);
    setShowModal(true);
  };

  const resetPhonePreference = () => {
    dispatch(
      patientActions.updatePatientNotificationPreference({
        phone: false,
        email: notificationPreference?.email,
        mpi,
      })
    );
  };

  const dispatchUpdateOrderAddress = (addressInput) => {
    setLoading(true);

    return dispatch(
      updateOrderAddress({
        mpi,
        orderId,
        addressInput,
      })
    )
      .then((response) => {
        setLoading(false);

        if (!isBoolean(response)) {
          setSuggestedAddresses(response);
          setShowAddressConfirmationModal(true);
        } else if (response) {
          enqueueSnackbar({ message: 'Order Address Updated Successfully!', variant: 'success' });
          dispatch(listPatientOrders({ mpi }));
          afterAddressUpdate();
          closeDialog();
          handleCloseModal();
        }
      })
      .catch(() => {
        setLoading(false);
        setShowAddressConfirmationModal(true);
      });
  };

  const handleEditOrderAddress = (formData) => {
    const isDisallowedState = !find(usStateAbbreviation, {
      abbreviation: formData.state,
    })?.isSMSAllowed;

    const addressInput = {
      ...omit(formData, 'line1', 'line2'),
      addressLine1: formData.line1,
      addressLine2: formData.line2,
      deliveryInstructions: formData.deliveryInstructions || undefined,
    };

    setSavedNewAddress(addressInput);

    showWarning({
      warn: formData.isDefault && isDisallowedState && notificationPreference?.phone,
      callback: () => dispatchUpdateOrderAddress(addressInput),
      warnCallback: () => {
        resetPhonePreference();
        return dispatchUpdateOrderAddress(formData);
      },
    });
  };

  const handleConfirmAddress = (formData) => {
    let addressToSave = {};

    if (formData.isManuallyVerified) {
      addressToSave = {
        ...savedNewAddress,
        nameOnAddress: savedNewAddress.nameOnAddress,
        isDefault: savedNewAddress.isDefault,
        isManuallyVerified: true,
        isValidationRequired: false,
      };
    } else {
      const selectedAddress = suggestedAddresses[formData.addressChoice];

      addressToSave = {
        ...omit(selectedAddress, 'line1', 'line2'),
        addressLine1: selectedAddress.line1,
        addressLine2: selectedAddress.line2,
        nameOnAddress: savedNewAddress.nameOnAddress,
        isDefault: inputAddress.isDefault,
        isManuallyVerified: true,
        isValidationRequired: false,
      };
    }
    return dispatchUpdateOrderAddress(addressToSave);
  };

  return (
    <>
      <Button
        disabled={disabled}
        sx={{
          px: 1,
          fontSize: '0.75rem',
          minHeight: '30px',
          lineHeight: 'normal',
        }}
        size='small'
        variant='outlined'
        color='secondary'
        onClick={openDialog}
      >
        Edit Address
      </Button>
      <InfoDialog />

      <Dialog
        open={showModal}
        aria-describedby='ActionItemTab-EditOrderAddressButton-header'
        fullWidth
      >
        <DialogTitle id='ActionItemTab-EditOrderAddressButton-header' sx={{ pb: 0 }}>
          {showAddressConfirmationModal ? 'Confirm Address' : 'Edit Address'}
        </DialogTitle>

        {showAddressConfirmationModal ? (
          <Form
            initialValues={{}}
            onSubmit={handleConfirmAddress}
            render={({ handleSubmit, invalid }) => (
              <form noValidate onSubmit={handleSubmit}>
                {!isEmpty(suggestedAddresses) ? (
                  <DialogContent>
                    <Typography variant='h6' sx={{ p: 0, pb: 1 }}>
                      We noticed that the address you entered may not be valid. Please select one
                      from the suggestions below.
                    </Typography>

                    <RadioGroupField
                      name='addressChoice'
                      id='ActionItemTab-EditOrderAddressButton-selectAddress-input'
                      label=''
                    >
                      <Stack direction='column' alignItems='flex-start'>
                        {map(suggestedAddresses, (address, index) => (
                          <FormControlLabel
                            key={`validateAddress-${index}`}
                            value={index}
                            control={<Radio sx={{ mr: 1 }} />}
                            label={
                              <ListItemText
                                primary={
                                  <>
                                    <Typography variant='h6'>{address.line1}</Typography>
                                    {address?.line2 && <Typography>{address.line2}</Typography>}
                                    <Typography>
                                      {address.city} , {address.state}, {address.zip}
                                    </Typography>
                                  </>
                                }
                              />
                            }
                          />
                        ))}
                      </Stack>
                    </RadioGroupField>
                  </DialogContent>
                ) : (
                  <DialogContent>
                    <Typography sx={{ mb: 3 }}>
                      We were unable to verify the entered address. Please review the address
                      carefully and confirm that you have verified its accuracy before proceeding.
                    </Typography>

                    <CheckboxField
                      required
                      id='ActionItemTab-EditOrderAddressButton-checkbox'
                      name='isManuallyVerified'
                      label={`${savedNewAddress.addressLine1}, ${
                        savedNewAddress.addressLine2 || ''
                      } ${savedNewAddress.city}, ${savedNewAddress.state}, ${savedNewAddress.zip}`}
                      disableHelperText
                    />
                  </DialogContent>
                )}

                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={closeDialog}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    disabled={invalid}
                    variant='contained'
                    type='submit'
                  >
                    Confirm
                  </LoadingButton>
                </DialogActions>
              </form>
            )}
          />
        ) : (
          <Form
            onSubmit={handleEditOrderAddress}
            initialValues={{ ...orderAddress, deliveryInstructions }}
            render={({ handleSubmit, invalid }) => (
              <form noValidate onSubmit={handleSubmit}>
                <DialogContent>
                  <TextField
                    id='ActionItemTab-EditOrderAddressButton-nameOnAddress-input'
                    label='Name On Address'
                    name='nameOnAddress'
                    required
                    autoFocus
                  />
                  <TextField
                    id='ActionItemTab-EditOrderAddressButton-addressLine1-input'
                    label='Address Line 1'
                    name='line1'
                    required
                    validations={[validateCombinedAddressLength('line1', 'line2')]}
                  />
                  <TextField
                    id='ActionItemTab-EditOrderAddressButton-addressLine2-input'
                    label='Address Line 2'
                    name='line2'
                    validations={[validateCombinedAddressLength('line1', 'line2')]}
                  />
                  <TextField
                    id='ActionItemTab-EditOrderAddressButton-city-input'
                    label='City'
                    name='city'
                    required
                  />

                  <SelectField
                    id='ActionItemTab-EditOrderAddressButton-state-dropdown'
                    label='State'
                    name='state'
                    required
                  >
                    {map(usStateAbbreviation, ({ name, abbreviation }) => (
                      <MenuItem value={abbreviation} key={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                  <TextField
                    id='ActionItemTab-EditOrderAddressButton-zip-input'
                    label='Zip'
                    name='zip'
                    type='zip'
                    required
                    maxLength={10}
                    format={formatZipCode}
                    validations={[validateZipCode()]}
                  />
                  <TextField
                    id='ActionItemTab-EditOrderAddressButton-deliveryInstructions-input'
                    label='Delivery Instructions'
                    name='deliveryInstructions'
                    maxLength={200}
                    multiline
                    rows={2}
                  />
                  <CheckboxField
                    label='Default'
                    name='isDefault'
                    id='ActionItemTab-EditOrderAddressButton-isDefault-checkbox'
                  />
                </DialogContent>

                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    variant='contained'
                    disabled={invalid}
                    type='submit'
                  >
                    Save
                  </LoadingButton>
                </DialogActions>
              </form>
            )}
          />
        )}
      </Dialog>
    </>
  );
};

EditOrderAddressButton.propTypes = {
  orderId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  orderAddress: PropTypes.shape({
    line1: PropTypes.string.isRequired,
    line2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
  }).isRequired,
  deliveryInstructions: PropTypes.string,
  afterAddressUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  notificationPreference: PropTypes.shape({
    phone: PropTypes.bool,
    email: PropTypes.bool,
  }),
};

EditOrderAddressButton.defaultProps = {
  deliveryInstructions: undefined,
  notificationPreference: undefined,
};

export default EditOrderAddressButton;
